import React, { useState, useEffect, useMemo } from 'react';
import { markActioned, getActionedSet } from './indexedDBStore.js';
import { thumbUrl } from './metaApiSource';
import { Icon } from './icons';
import PerfBlockHeader from './PerfBlockHeader';
import { RULES_V2_START } from './performanceRules';
import { AD_PREFIX_RE } from '../creatorType';
import { isV2Name } from './nomenclatureParser';

// Winner tags (performanceRules.findTaggedWinners): which rule the qualifying run meets.
const TAGS = [
  { key: 'NEW', label: 'New', badge: 'NEW', bg: '#FF7900', fg: '#fff' },
  { key: 'STILL_VALID', label: 'Still valid', badge: 'STILL VALID', bg: '#f1f1f1', fg: '#333' },
  { key: 'LEGACY', label: 'Legacy', badge: 'LEGACY', bg: '#e5e5e5', fg: '#888' },
];
const TAG_BY_KEY = Object.fromEntries(TAGS.map(t => [t.key, t]));
const PUSHABLE = ['NEW', 'STILL_VALID'];
const fmtDay = (d) => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '';

function TagBadge({ tag, style }) {
  const t = TAG_BY_KEY[tag];
  if (!t) return null;
  return <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.4px', background: t.bg, color: t.fg, whiteSpace: 'nowrap', ...style }}>{t.badge}</span>;
}

// Export the winners list as a CSV (Excel-FR friendly: ';' separator + UTF-8 BOM).
function exportWinnersCSV(winners, actionedSet) {
  const esc = (v) => {
    const str = (v === null || v === undefined) ? '' : String(v);
    return /[";\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
  };
  const parseCreator = (name) => {
    const m = (name || '').match(/^\d+_[a-z]+_[a-z]+-([^_]+)/i);
    return m ? m[1].replace(/-/g, ' ') : '';
  };
  const header = ['ad_id', 'core_id', 'ad_name', 'creator', 'feature', 'category', 'tag', 'qualifying_run', 'achats', 'CPA', 'spend', 'status', 'actioned'];
  const lines = (winners || []).map((w) => [
    w.ad_id || w.metaAdId || '',
    w.core_id || '',
    w.ad_name_sample || '',
    (w.nomenclature && w.nomenclature.creator) || parseCreator(w.ad_name_sample),
    (w.nomenclature && w.nomenclature.feature) || w.feature || '',
    (w.nomenclature && w.nomenclature.category) || w.adset_category || '',
    w.winner_tag || '',
    w.qualifying_date || '',
    w.achats != null ? w.achats : 0,
    (w.cpa != null) ? Number(w.cpa).toFixed(2) : '',
    Math.round(w.spend || 0),
    w.status || 'WINNER',
    (actionedSet && actionedSet.has && actionedSet.has(w.core_id)) ? 'oui' : 'non',
  ].map(esc).join(';'));
  const csv = '\uFEFF' + header.join(';') + '\n' + lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'timbo-winners-' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// Extract a short, distinctive name from a full ad name
// Example: "17530_yann_influencer-CadelJenkins_shampulong-5_HOOK-ugc_DUR-15_PERSON"
//       -> "CadelJenkins_shampulong-5"
function shortNameForSlack(fullName) {
  if (!fullName) return 'Unnamed ad';
  // Strip the leading id prefix like "17530_yann_influencer-"
  let s = fullName.replace(AD_PREFIX_RE, '');
  // Cut at "_HOOK" or "_DUR" or "_ugc" — those are technical suffixes
  const cutAt = s.search(/_HOOK|_DUR|_ugc/i);
  if (cutAt > 0) s = s.slice(0, cutAt);
  return s.slice(0, 50);
}

// Simplified AdRow inline to eliminate import-related issues
function SimpleAdRow({ ad, selected, actioned, onToggleSelect, onMarkActioned, onPlay }) {
  const shortName = (ad.ad_name_sample || '').replace(AD_PREFIX_RE, '').slice(0, 70);
  const seqMatch = (ad.ad_name_sample || '').match(/^(\d+)_/);
  const seqId = seqMatch ? seqMatch[1] : (ad.core_id || '');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
      borderBottom: '1px solid #f5f5f5', background: selected ? 'rgba(255,121,0,0.04)' : actioned ? '#fafafa' : '#fff',
      opacity: actioned ? 0.4 : ad.winner_tag === 'LEGACY' ? 0.6 : 1, cursor: 'pointer',
    }}>
      <div onClick={() => onPlay && ad.ad_id && onPlay(ad.ad_id)} style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: ad.ad_id ? 'pointer' : 'default' }}>
        {thumbUrl(ad)
          ? <img src={thumbUrl(ad)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#bbb', fontSize: 13 }}>▶</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <TagBadge tag={ad.winner_tag} style={{ marginRight: 6 }} /><span style={{ fontFamily: "'DM Mono', monospace", color: '#FF7900', fontWeight: 700, userSelect: 'all' }}>{seqId}</span> {shortName}
        </div>
        {ad.qualifying_date && <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>Run {fmtDay(ad.qualifying_date)}</div>}
      </div>
      <div style={{ display: 'flex', gap: 16, flexShrink: 0, fontSize: 12 }}>
        <div style={{ textAlign: 'right' }}><div style={{ fontWeight: 500 }}>{ad.achats ?? 0}</div><div style={{ fontSize: 9, color: '#888' }}>purch.</div></div>
        <div style={{ textAlign: 'right' }}><div style={{ fontWeight: 500 }}>€{ad.cpa ? ad.cpa.toFixed(1) : '—'}</div><div style={{ fontSize: 9, color: '#888' }}>CPA</div></div>
        <div style={{ textAlign: 'right' }}><div style={{ fontWeight: 500 }}>€{Math.round(ad.spend || 0)}</div><div style={{ fontSize: 9, color: '#888' }}>spend</div></div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button onClick={onMarkActioned} style={{ width: 28, height: 28, border: '1px solid #e5e5e5', borderRadius: 6, background: actioned ? '#f0fdf4' : '#fff', color: actioned ? '#16a34a' : '#888', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</button>
      </div>
    </div>
  );
}

function WinnerGridCard({ ad, selected, actioned, onToggleSelect, onMarkActioned, onPlay }) {
  const t = thumbUrl(ad);
  const name = (ad.ad_name_sample || '').replace(AD_PREFIX_RE, '').split(/_HOOK|_DUR|_ugc/i)[0].replace(/_/g, ' ').slice(0, 40);
  const seqMatch = (ad.ad_name_sample || '').match(/^(\d+)_/);
  const seqId = seqMatch ? seqMatch[1] : (ad.core_id || '');
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', background: '#fff', opacity: actioned ? 0.5 : ad.winner_tag === 'LEGACY' ? 0.6 : 1 }}>
      <div onClick={() => onPlay && ad.ad_id && onPlay(ad.ad_id)} style={{ position: 'relative', aspectRatio: '9 / 16', background: '#f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t
          ? <img src={t} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#bbb', fontSize: 26 }}>▶</span>}
        <TagBadge tag={ad.winner_tag} style={{ position: 'absolute', top: 8, left: 8 }} />
        {t && <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, pointerEvents: 'none' }}>▶</span>}
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, color: '#FF7900', userSelect: 'all' }} title="ID — copy for the Main adset">{seqId}</span>
          {ad.qualifying_date && <span style={{ fontSize: 10, color: '#999' }} title="Date of the qualifying test run">Run {fmtDay(ad.qualifying_date)}</span>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }} title={ad.ad_name_sample}>{name || ('#' + ad.core_id)}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11 }}>
          <span><b>{ad.achats ?? 0}</b> <span style={{ color: '#999' }}>purch.</span></span>
          <span><b>€{ad.cpa ? ad.cpa.toFixed(1) : '-'}</b> <span style={{ color: '#999' }}>CPA</span></span>
          <span><b>€{Math.round(ad.spend || 0)}</b></span>
        </div>
        <button onClick={onMarkActioned} style={{ width: '100%', marginTop: 8, padding: '5px 0', borderRadius: 7, border: '1px solid #e5e5e5', background: actioned ? '#f0fdf4' : '#fff', color: actioned ? '#16a34a' : '#555', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{actioned ? 'Done' : 'Mark actioned'}</button>
      </div>
    </div>
  );
}

// True when the name needs no category assignment: it has a _CAT- block, or it is a v2 name (no CAT block by design).
function hasCategory(name) { return /_CAT-[a-z0-9]+/i.test(name || '') || isV2Name(name); }
function buildRenamedName(oldName, cluster) {
  if (!oldName) return oldName;
  if (isV2Name(oldName)) return oldName; // v2 block layout is fixed — never rename it
  const token = '_CAT-' + cluster;
  if (hasCategory(oldName)) return oldName.replace(/_CAT-[a-z0-9]+/i, token);
  const bracket = oldName.search(/_?\[/);
  if (bracket > -1) return oldName.slice(0, bracket) + token + oldName.slice(bracket);
  const dateM = oldName.match(/_(\d{8})$/);
  if (dateM) return oldName.slice(0, dateM.index) + token + oldName.slice(dateM.index);
  return oldName + token;
}
function exportWinnersByCategory(winners, renames) {
  // Google-Sheet friendly: STACKED blocks — one section header per category, names listed below, single column.
  const CATS = [['fashion', 'Fashion'], ['beauty', 'Beauty'], ['food', 'Food & Beverage'], ['home', 'Home'], ['other', 'Other']];
  const keyOfW = (w) => w.core_id || w.ad_name_sample;
  const catOf = (w) => { const ex = (w.ad_name_sample || '').match(/_CAT-([a-z0-9]+)/i); if (ex) return ex[1].toLowerCase(); const r = renames[keyOfW(w)]; return (r && r.cluster) ? r.cluster : null; };
  const nameOf = (w) => { const r = renames[keyOfW(w)]; return (r && r.newName) || w.ad_name_sample || ''; };
  const esc = (v) => { const str = (v === null || v === undefined) ? '' : String(v); return /[";\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str; };
  const lines = [];
  CATS.forEach(([k, label]) => {
    const names = (winners || []).filter((w) => catOf(w) === k).map(nameOf);
    if (names.length === 0) return;
    lines.push(esc(label.toUpperCase() + ' (' + names.length + ')'));
    names.forEach((n) => lines.push(esc(n)));
    lines.push('');
  });
  // v2 names carry no category: listed on their own so they are not silently dropped from the export.
  const v2Names = (winners || []).filter((w) => isV2Name(w.ad_name_sample) && catOf(w) === null).map(nameOf);
  if (v2Names.length) {
    lines.push(esc('NO CATEGORY IN NAME — V2 (' + v2Names.length + ')'));
    v2Names.forEach((n) => lines.push(esc(n)));
    lines.push('');
  }
  const csv = '﻿' + lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'timbo-winners-by-category-' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function WinnersToPromoteCard({ taggedWinners, kpis, onPlay, onSendToUA }) {
  const [selected, setSelected] = useState(new Set());
  const [actionedSet, setActionedSet] = useState(new Set());
  const [renames, setRenames] = useState({});
  const [showCatModal, setShowCatModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    getActionedSet().then(s => setActionedSet(s)).catch(() => {});
  }, []);

  // Default view = the pile to push today (New + Still valid). Legacy is history, kept apart.
  const [tagFilter, setTagFilter] = useState(() => new Set(PUSHABLE));
  const safeWinners = Array.isArray(taggedWinners) ? taggedWinners : []; // 6-month window, scoped upstream
  const tagCounts = useMemo(() => {
    const c = { NEW: 0, STILL_VALID: 0, LEGACY: 0 };
    for (const w of safeWinners) if (c[w.winner_tag] != null) c[w.winner_tag]++;
    return c;
  }, [safeWinners]);
  const pushableCount = tagCounts.NEW + tagCounts.STILL_VALID;
  // Already sorted by qualifying run date, newest first (findTaggedWinners).
  const filteredWinners = useMemo(() => safeWinners.filter(w => tagFilter.has(w.winner_tag)), [safeWinners, tagFilter]);
  const totalCount = filteredWinners.length;
  const allOn = TAGS.every(t => tagFilter.has(t.key));
  const toggleTag = (k) => setTagFilter(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n.size ? n : new Set([k]); });
  const [collapsedMonths, setCollapsedMonths] = useState(() => new Set());
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const monthGroups = useMemo(() => {
    const keyOf = (ad) => { const d = ad.qualifying_date || ad.first_date || ad.last_date; return (d && d.length >= 7) ? d.slice(0, 7) : 'undated'; };
    const labelOf = (k) => { if (k === 'undated') return 'Undated'; const [y, m] = k.split('-'); return new Date(Date.UTC(+y, +m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }); };
    const map = new Map();
    for (const ad of filteredWinners) { const k = keyOf(ad); if (!map.has(k)) map.set(k, []); map.get(k).push(ad); }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([key, ads]) => ({ key, label: labelOf(key), ads }));
  }, [filteredWinners]);
  const toggleMonth = (k) => setCollapsedMonths(pr => { const n = new Set(pr); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleExpandMonth = (k) => setExpandedMonths(pr => { const n = new Set(pr); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const kpiRate = kpis?.promotionRate ?? 0;

  const toggleSel = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    const all = filteredWinners.length > 0 && filteredWinners.every(a => selected.has(a.core_id));
    setSelected(p => { const n = new Set(p); if (all) filteredWinners.forEach(a => n.delete(a.core_id)); else filteredWinners.forEach(a => n.add(a.core_id)); return n; });
  };
  const doMarkActioned = async (id) => {
    try { await markActioned(id); } catch (e) { console.warn('markActioned err:', e); }
    setActionedSet(p => new Set([...p, id]));
    setSelected(p => { const n = new Set(p); n.delete(id); return n; });
  };
  const doSend = async () => {
    const ads = filteredWinners.filter(a => selected.has(a.core_id));
    if (ads.length === 0) return;

    const webhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL;

    if (!webhookUrl) {
      console.error('REACT_APP_SLACK_WEBHOOK_URL not configured');
      const msg = generateMsg(ads);
      try { await navigator.clipboard.writeText(msg); } catch (e) {}
      setToast({ type: 'error', text: '⚠ Slack not configured · Message copied to clipboard instead' });
      setTimeout(() => setToast(null), 4500);
      return;
    }

    setToast({ type: 'loading', text: testMode ? 'Sending test message…' : 'Sending to Slack…' });

    try {
      const slackPayload = generateSlackPayload(ads, testMode);
      // NOTE: No Content-Type header to avoid CORS preflight (Slack rejects OPTIONS preflight)
      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: JSON.stringify(slackPayload),
      });

      if (!response.ok) {
        throw new Error('Slack returned ' + response.status);
      }

      // In test mode: don't mark ads as actioned, so they remain re-testable
      if (!testMode) {
        for (const a of ads) {
          try { await markActioned(a.core_id); } catch (e) { console.warn('markActioned err:', e); }
        }
        setActionedSet(p => { const n = new Set(p); ads.forEach(a => n.add(a.core_id)); return n; });
      }
      setSelected(new Set());
      const successText = testMode
        ? '✓ Test sent · ' + ads.length + (ads.length === 1 ? ' ad (not marked)' : ' ads (not marked)')
        : '✓ Sent to #marketing_influencers · ' + ads.length + (ads.length === 1 ? ' winner' : ' winners');
      setToast({ type: 'success', text: successText });
      setTimeout(() => setToast(null), 3500);

    } catch (err) {
      console.error('Slack send failed:', err);
      setToast({ type: 'error', text: '✗ Failed to send to Slack · Try again' });
      setTimeout(() => setToast(null), 4500);
    }
  };

  return (
    <div className="tb-card winners-block">
      <PerfBlockHeader
        icon={<Icon name="winners" />}
        title="Winners to Promote"
        subtitle="≥5 purchases in a single run · CPA judged in BAU"
        actions={<>
          {onSendToUA && pushableCount > 0 && (
            <button type="button" className="perf-hctrl" onClick={onSendToUA} style={{ background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}>📤 Send to UA · {pushableCount}</button>
          )}
          <button type="button" className="perf-hctrl" onClick={() => setShowCatModal(true)}>🏷 Categories</button>
          <button type="button" className="perf-hctrl" onClick={() => exportWinnersCSV(filteredWinners, actionedSet)}>⤓ Detail CSV</button>
          <div className="perf-hicons">
            {['grid', 'list'].map(m => (
              <button key={m} onClick={() => setViewMode(m)} title={m} className={viewMode === m ? 'on' : ''}>
                {m === 'grid' ? '▦' : '≡'}
              </button>
            ))}
          </div>
        </>}
      />

      {/* Content */}
      <div className="winners-content">
      {/* KPIs with sublabels */}
      {/* Lost potential removed: its inputs (survival × scale × LTV) were never calibrated. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div className="fnl-card fnl-card-static"><div className="fnl-l">Push to Main adset</div><div className="fnl-v">{pushableCount}</div><div className="fnl-s">New + Still valid · ≥5 purchases in one run · not in Main</div></div>
        <div className="fnl-card fnl-card-static"><div className="fnl-l">Promotion rate</div><div className="fnl-v">{kpiRate}%</div><div className="fnl-s">target: 60%+ · winners promoted</div></div>
      </div>

      {/* Tag filter — default New + Still valid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {TAGS.map(t => {
          const on = tagFilter.has(t.key);
          return (
            <button key={t.key} type="button" onClick={() => toggleTag(t.key)} aria-pressed={on}
              style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (on ? '#1a1a1a' : '#e5e5e5'), background: on ? '#1a1a1a' : '#fff', color: on ? '#fff' : '#555' }}>
              {t.label} <span style={{ opacity: 0.6 }}>{tagCounts[t.key]}</span>
            </button>
          );
        })}
        <button type="button" onClick={() => setTagFilter(allOn ? new Set(PUSHABLE) : new Set(TAGS.map(t => t.key)))} aria-pressed={allOn}
          style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (allOn ? '#1a1a1a' : '#e5e5e5'), background: allOn ? '#1a1a1a' : '#fff', color: allOn ? '#fff' : '#555' }}>
          All <span style={{ opacity: 0.6 }}>{safeWinners.length}</span>
        </button>
      </div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
        New = qualifying run since {fmtDay(RULES_V2_START)} · Still valid = earlier run that passes today's rule · Legacy = qualified under the previous rule (≥3 purchases · CPA ≤ €35 · spend ≥ €80), not today's.
      </div>

      {showCatModal && (
        <div onClick={() => setShowCatModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 1000, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <PerfBlockHeader
              variant="modal"
              title="Categories & export"
              actions={<button type="button" className="perf-hctrl" onClick={() => exportWinnersByCategory(filteredWinners, renames)}>⤓ Export CSV (Google Sheet)</button>}
              onClose={() => setShowCatModal(false)}
            />
            <div style={{ padding: 18, overflowY: 'auto' }}>
              {(() => {
                const noCat = filteredWinners.filter((w) => !hasCategory(w.ad_name_sample));
                if (noCat.length === 0) return <div style={{ color: '#16a34a', fontSize: 13 }}>✓ All creatives have a category. Ready to export.</div>;
                return (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706', marginBottom: 10 }}>⚠ {noCat.length} créa(s) sans catégorie — assigne-les :</div>
                    {noCat.map((w) => {
                      const key = w.core_id || w.ad_name_sample;
                      const r = renames[key];
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f5ecd8', fontSize: 12 }}>
                          <div onClick={() => onPlay && w.ad_id && onPlay(w.ad_id)} style={{ width: 44, height: 44, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f0f0f0', cursor: w.ad_id ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {thumbUrl(w) ? <img src={thumbUrl(w)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#bbb', fontSize: 12 }}>▶</span>}
                          </div>
                          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={w.ad_name_sample}>{w.ad_name_sample}</span>
                          <select value={r ? r.cluster : ''} onChange={(e) => {
                            const cluster = e.target.value;
                            setRenames((prev) => { const n = { ...prev }; if (!cluster) { delete n[key]; } else { n[key] = { ad_id: w.ad_id || w.metaAdId || '', cluster, oldName: w.ad_name_sample || '', newName: buildRenamedName(w.ad_name_sample, cluster) }; } return n; });
                          }} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e5e5', fontSize: 12 }}>
                            <option value="">Assign category…</option>
                            <option value="fashion">Fashion</option>
                            <option value="beauty">Beauty</option>
                            <option value="food">Food & Beverage</option>
                            <option value="home">Home</option>
                            <option value="other">Other</option>
                          </select>
                          {r && <span style={{ color: '#8a6d3b', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }} title={r.newName}>→ {r.newName}</span>}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Ads list / grid — grouped by month, collapsible */}
      <div>
        {totalCount === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>No winners in this view</div>
        ) : monthGroups.map(g => {
          const isCollapsed = collapsedMonths.has(g.key);
          const shown = expandedMonths.has(g.key) ? g.ads : g.ads.slice(0, 6);
          const rest = g.ads.length - shown.length;
          return (
            <div key={g.key} style={{ borderTop: '1px solid #f0f0f0' }}>
              <div onClick={() => toggleMonth(g.key)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 2px', cursor: 'pointer', userSelect: 'none' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#555', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: '#bbb', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }}>▾</span>{g.label}
                </span>
                <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>{g.ads.length}</span>
              </div>
              {!isCollapsed && (viewMode === 'grid' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, padding: '2px 0 10px' }}>
                  {shown.map((ad, idx) => (
                    <WinnerGridCard key={ad.core_id || idx} ad={ad}
                      selected={selected.has(ad.core_id)} actioned={actionedSet.has(ad.core_id)}
                      onToggleSelect={() => toggleSel(ad.core_id)} onMarkActioned={() => doMarkActioned(ad.core_id)} onPlay={onPlay} />
                  ))}
                </div>
              ) : (
                <div style={{ paddingBottom: 6 }}>
                  {shown.map((ad, idx) => (
                    <SimpleAdRow key={ad.core_id || idx} ad={ad}
                      selected={selected.has(ad.core_id)} actioned={actionedSet.has(ad.core_id)}
                      onToggleSelect={() => toggleSel(ad.core_id)} onMarkActioned={() => doMarkActioned(ad.core_id)} onPlay={onPlay} />
                  ))}
                </div>
              ))}
              {!isCollapsed && rest > 0 && (
                <div onClick={() => toggleExpandMonth(g.key)} style={{ color: '#FF7900', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '0 2px 12px' }}>
                  {expandedMonths.has(g.key) ? '▴ Show less' : '▾ + ' + rest + ' more'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#dc2626' : toast.type === 'loading' ? '#1a1a1a' : '#171717',
          color: '#fff',
          padding: '12px 20px', borderRadius: 8, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 10,
          zIndex: 2000, boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
        }}>
          {toast.type === 'loading' && <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }}></span>}
          <span>{toast.text}</span>
        </div>
      )}
      </div>{/* end winners-content */}
    </div>
  );
}

function generateMsg(ads) {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  let msg = ads.length + (ads.length === 1 ? ' winner' : ' winners') + ' to promote — ' + date + '\n\n';
  ads.forEach((ad) => {
    const name = shortNameForSlack(ad.ad_name_sample);
    const cpa = ad.cpa ? Math.round(ad.cpa) : '?';
    const purchases = ad.achats || 0;
    msg += '• ' + ad.core_id + ' — ' + name + '  ·  ' + purchases + ' purchases · €' + cpa + ' CPA\n';
  });
  return msg;
}

function generateSlackPayload(ads, testMode) {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Header (with optional [TEST] prefix)
  const prefix = testMode ? '[TEST] ' : '';
  let text = prefix + '*' + ads.length + (ads.length === 1 ? ' winner' : ' winners') + ' to promote* — ' + date + '\n\n';

  // List of ads (ID first, then short name, then key stats)
  ads.forEach((ad) => {
    const name = shortNameForSlack(ad.ad_name_sample);
    const cpa = ad.cpa ? Math.round(ad.cpa) : '?';
    const purchases = ad.achats || 0;
    text += '• `' + ad.core_id + '` — ' + name + '  ·  ' + purchases + ' purchases · €' + cpa + ' CPA\n';
  });

  return { text: text };
}
